import { ReadonlyVec2, vec2 } from 'gl-matrix';
import type { Image3D } from '~/domain/patient/image3d';
import { gl } from '../Engine';
import { TextureSource } from '../core/models/TextureSource';
import { Observable } from '../core/observable';
import { RenderStorage } from '../core/render/RenderStorage';
import { CrossSliceDiffRenderer } from '../core/render/renderers/CrossSliceDiffRenderer';
import { QuadRenderer } from '../core/render/renderers/QuadRenderer';
import { FrameBufferObject } from '../core/utils/FrameBufferObject';
import { Environment } from '../data/Environment';
import { CustomObject } from '../data/common/customs/CustomObject';
import { EventHandler } from '../tool/EventHandler';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../tool/EventHandlerResult';
import { ModifierKeys } from '../tool/ModifierKeys';
import { isSelectedStructurePeeked } from './layers/contourAlpha';
import {
    FusedImageDisplay,
    resolveFusedImageSlots,
} from './layers/fusedImageDisplay';
import { Layer, LayerBitMask, LayerClass } from './layers/Layer';
import { LayerBit } from './layers/LayerBit';

/**
 * An image composited into the frame currently published on a view's canvas.
 *
 * `null` marks a source whose image is not known. It fails safe: it can never be
 * affirmed free of burned-in identifiers, nor vetted by the user, because there is
 * no image to vet.
 *
 * No writer currently produces `null` — {@link renderLayers} is the only path that
 * publishes a frame, and it commits the composited {@link Image3D}s. It is the
 * representation any path that publishes pixels of unknown provenance (a ready-made
 * bitmap, a decoded server-side render) must use: the screenshot PHI gate fails safe
 * on it, so such a path cannot silently inherit the previous frame's verdict.
 */
export type CanvasFrameSource = Image3D | null;

/**
 * Draws `layers` in order onto the bound framebuffer, each over what is below
 * by its straight alpha: a texel's colour c at opacity a lands as
 * c·a + below·(1 − a). Leaves that blend function enabled on return.
 */
export function compositeLayers(layers: Iterable<TextureSource>): void {
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    const renderer = RenderStorage.getRenderer(QuadRenderer);
    for (const layer of layers) {
        renderer.render(layer);
    }
}

/**
 * Fills the selected structure's cross-slice difference onto the bound
 * framebuffer, while that overlay is on
 * ({@link Environment.crossSliceDiffEnabled}).
 *
 * Draws nothing while the selected structure is peeked: the fill lies on the
 * boundary its contour is withheld to uncover. A peek that names another
 * structure than the selected one withholds nothing.
 *
 * The structure's texture is resolved before the diff renderer is fetched,
 * because lazy SDF generation switches the GL program.
 */
export function drawCrossSliceDiff(
    view: TextureView,
    environment: Environment
): void {
    if (!environment.crossSliceDiffEnabled) {
        return;
    }
    const selectedStructure = environment.structureSet?.selectedStructure;
    if (
        isSelectedStructurePeeked(
            selectedStructure?.roiNumber,
            environment.peekedRoiNumber
        )
    ) {
        return;
    }
    const contourTexture = selectedStructure?.drawableTexture;
    if (contourTexture) {
        RenderStorage.getRenderer(CrossSliceDiffRenderer).render(
            view,
            contourTexture
        );
    }
}

/**
 * Abstract base class for the WebGL views — a visualization of data rendered onto
 * its own {@link HTMLCanvasElement canvas}.\
 * {@link TextureView TextureViews} consist of {@link Layer Layers} and each {@link Layer} may be updated separately.\
 * {@link TextureView} also has it's own {@link WebGLTexture texture} that contains the view's last rendered frame.
 *
 * @implements Interface {@link EventHandler} as a view may define it's own EventHandlers.\
 * {@link ToolManager} will propagate the events all the way down to the view if {@link ToolManager.activeAction} and {@link ToolManager.activeTool} do not prevent the propagation.
 */
export abstract class TextureView
    implements EventHandler, Iterable<Layer<unknown>>
{
    private _fusedImageDisplay: FusedImageDisplay = FusedImageDisplay.Blended;

    /**
     * Notifies the view itself after it changes which of a fused pair it
     * draws ({@link fusedImageDisplay}), once it has redrawn. Setting the
     * display it already has notifies nothing.
     */
    public readonly fusedImageDisplayChanged = new Observable<TextureView>();

    /** Which of a fused pair this view draws. */
    public get fusedImageDisplay(): FusedImageDisplay {
        return this._fusedImageDisplay;
    }

    public set fusedImageDisplay(display: FusedImageDisplay) {
        if (this._fusedImageDisplay === display) {
            return;
        }
        this._fusedImageDisplay = display;
        // The side drawn decides the image and whether the dose is drawn.
        this.updateView(LayerBit.IMAGE_LAYER_BIT | LayerBit.DOSE_LAYER_BIT);
        this.fusedImageDisplayChanged.notify(this);
    }

    /**
     * True only for {@link PlaneView}. Lets code that cannot import PlaneView
     * — the engine tools sit in an import cycle with it through ViewManager —
     * narrow a view without an `instanceof`.
     */
    public get isPlaneView(): boolean {
        return false;
    }

    /**
     * The {@link HTMLCanvasElement} that is used to display this view's contents.\
     * This view will use the whole canvas as its own.
     */
    private readonly _canvas: HTMLCanvasElement;

    private resizeObserver?: ResizeObserver;

    private _canvas2D: HTMLCanvasElement;

    /**
     * All the {@link Layer Layers} this {@link TextureView} contains, in the
     * insertion order of {@link defaultLayers} — which is also their composite
     * order, so it is load-bearing rather than incidental.
     */
    protected readonly layers: readonly Layer<unknown>[];

    /**
     * {@link FrameBufferObject} that is used to update the contents of this {@link TextureView}.\
     * The {@link WebGL2RenderingContext.COLOR_ATTACHMENT0 COLOR_ATTACHMENT0} of that {@link FrameBufferObject} stores the texture data of this {@link TextureView}.
     */
    private readonly _frameBufferObject: FrameBufferObject;

    /**
     * The {@link ImageBitmapRenderingContext Bitmap renderer} that can be used to efficiently render a Bitmap\
     * generated on the {@link WebGL2RenderingContext WebGLContext} onto the {@link canvas}.
     */
    private bitmapRenderer?: ImageBitmapRenderingContext;

    /** Backing field for {@link canvasFrameSources}. */
    private _canvasFrameSources: readonly CanvasFrameSource[] = [];

    /** Backing field for {@link canvasFrameId}. */
    private _canvasFrameId = 0;

    /**
     * The constructor takes the {@link HTMLCanvasElement canvases} this view renders onto.\
     * The {@link canvas}-parameter is also used to initialize the {@link frameBufferObject}\
     * and to observe size changes ({@link onCanvasResize}).\
     * This constructor also populates {@link layers} with {@link defaultLayers}.
     */
    public constructor(
        protected readonly env: Environment,
        canvas: HTMLCanvasElement,
        canvas2D: HTMLCanvasElement
    ) {
        this._canvas = canvas;
        this._canvas2D = canvas2D;
        this.env = env;
        this.layers = this.initializeDefaultLayers();
        if (canvas) {
            this.bitmapRenderer =
                canvas.getContext('bitmaprenderer') || undefined;
        }

        this._frameBufferObject = new FrameBufferObject(this.size);

        // Observation starts only once every field a resize notification
        // touches is in place. The notification runs `onCanvasResize` ->
        // `resize`, which dereferences `_frameBufferObject` unguarded, so
        // observing earlier would leave a window in which a delivered resize
        // throws. A real `ResizeObserver` delivers asynchronously and would not
        // reach that window, but that is a property of the host, not of this
        // class; a synchronous observer stub reaches it directly.
        this.resizeObserver = new ResizeObserver(this.resizeObserve);
        if (this._canvas) {
            this.resizeObserver.observe(this._canvas);
        }
    }

    private resizeObserve = (): void => {
        this.onCanvasResize();
    };

    /**
     * Method to get reference to the {@link HTMLCanvasElement} this view is using.
     */
    public get canvas(): HTMLCanvasElement | undefined {
        return this._canvas;
    }

    public get get2DCanvas(): HTMLCanvasElement | null {
        return this._canvas2D;
    }

    /**
     * Method to convert the {@link ReadonlyVec2 client pixel coordinates} to {@link ReadonlyVec2 view centric coordinates}.\
     * The {@link ReadonlyVec2 client pixel coordinates} of the {@link coordinates}-parameter are in range [0, numberOfPixels].\
     * The {@link ReadonlyVec2 viewRelativeCoordinates} this method produces are in between [-1, 1].
     * @param coordinates {@link ReadonlyVec2 client pixel coordinates} to be converted.
     * @returns The view relative coordinates.
     */
    public clientToViewCoordinates(coordinates: ReadonlyVec2): vec2 {
        if (this._canvas) {
            const rect = this._canvas.getBoundingClientRect();
            const x =
                rect.left < rect.right
                    ? (coordinates[0] - rect.left) / (rect.right - rect.left)
                    : (coordinates[0] - rect.right) / (rect.left - rect.right);
            const y =
                rect.bottom < rect.top
                    ? (coordinates[1] - rect.bottom) / (rect.top - rect.bottom)
                    : (coordinates[1] - rect.top) / (rect.bottom - rect.top);
            //x and y are in range [0, 1], but we want them in range [-1, 1]. Note that y is flipped.
            return vec2.fromValues(2 * x - 1, -2 * y + 1);
        }
        return vec2.create();
    }

    /**
     * Convert client coordinates (in px) into canvas coordinates (in px)
     * @param coordinates
     * @returns
     */
    public clientToCanvas(clientCoordinates: ReadonlyVec2): ReadonlyVec2 {
        if (this._canvas) {
            const rect = this._canvas.getBoundingClientRect();
            const x = clientCoordinates[0] - rect.left;
            const y = clientCoordinates[1] - rect.top;
            return vec2.fromValues(x, y);
        }
        throw new Error('Canvas is undefined');
    }

    /**
     * Method to get the size of this view in pixels.\
     * This is equal to the pixel size of the underlying {@link HTMLCanvasElement canvas}.
     * @returns The size of this view in pixels.\
     * If {@link _canvas canvas} is undefined, returns vec2(0, 0).
     */
    public get size(): vec2 {
        return this._canvas
            ? vec2.fromValues(
                  this._canvas.clientWidth,
                  this._canvas.clientHeight
              )
            : vec2.fromValues(0, 0);
    }

    /**
     * Method to update the size of this view, its {@link _frameBufferObject} and its {@link Layer Layers}.\
     * Once those have been updated, the texture contents are also updated to visualize the changes.
     * @param size The new size of this {@link TextureView} in pixels.
     * @returns Reference to this.
     */
    public resize(size: ReadonlyVec2, env = this.env): TextureView {
        if (this._frameBufferObject) {
            if (vec2.equals(this._frameBufferObject.size, size)) {
                //if the size equals the old size, do nothing.
                return this;
            }
            this._frameBufferObject.resize(size);
        }
        gl.viewport(
            0,
            0,
            this._frameBufferObject.width,
            this._frameBufferObject.height
        );
        for (const layer of this) {
            layer.resize(size);
        }
        this.updateLayers(env);
        return this;
    }

    /**
     * Method performed whenever the {@link canvas} is resized.
     */
    protected onCanvasResize(): void {
        if (this.canvas) {
            this.resize(
                vec2.fromValues(
                    this.canvas.clientWidth,
                    this.canvas.clientHeight
                ),
                this.env
            );
        }
    }

    /**
     * Method that will initialize all the {@link defaultLayers} for this {@link TextureView view}.
     * @returns The {@link Layer Layers}, in {@link defaultLayers} order.
     */
    protected initializeDefaultLayers(): Layer<unknown>[] {
        return this.defaultLayers.map(
            (layerClass) => new layerClass(this, this.env)
        );
    }

    /**
     * An abstract method to get an Array of {@link LayerClass LayerClasses}.\
     * These {@link LayerClass LayerClasses} define the types of the {@link Layer Layers}, that {@link TextureView Views} of this type should be constructed with.
     */
    public abstract get defaultLayers(): LayerClass<unknown>[];

    public get frameBufferObject(): FrameBufferObject | undefined {
        return this._frameBufferObject;
    }

    public get environment(): Environment {
        return this.env;
    }

    /**
     * The DICOM image(s) baked into the frame on {@link canvas} right now. Read by
     * the viewport-screenshot PHI gate so its verdict describes the serialised
     * pixels, not the live {@link environment} — the two can diverge for the ~1
     * frame between an image swap and the async transfer that publishes the new
     * frame.
     *
     * The sources rather than their verdicts are committed because a source's
     * burned-in status is a pure, immutable function of its DICOM tags, so it can
     * be derived later without losing the frame binding — while the identity is
     * what lets the UI remember which images the user already vetted.
     */
    public get canvasFrameSources(): readonly CanvasFrameSource[] {
        return this._canvasFrameSources;
    }

    /**
     * Monotonic id of the frame currently published on {@link canvas}, bumped
     * once each time a new frame is committed — in lockstep with
     * {@link canvasFrameSources}, inside the same async transfer. Unlike the
     * burned-in *verdict*, it moves on *every* republish: a same-image re-render,
     * or a swap to a different image that happens to share a verdict, both change
     * it. That lets a screenshot bind its bytes to exactly one frame by asserting
     * the id did not move across the async capture (see
     * {@link downloadViewportScreenshot}) — a check a verdict comparison cannot make.
     */
    public get canvasFrameId(): number {
        return this._canvasFrameId;
    }

    private readonly _customObjects: CustomObject[] = [];
    public addCustomObject(obj: CustomObject) {
        this._customObjects.push(obj);
    }

    public removeCustomObject(obj: CustomObject) {
        const index = this._customObjects.indexOf(obj);
        if (index >= 0) {
            const ref = this._customObjects[index];
            this._customObjects.splice(index, 1);
            ref.delete();
        }
    }

    /**
     * Method to render this {@link TextureView}'s {@link TextureView.layers Layers}:
     * {@link compositeLayers composites} them, then draws the overlays on top.
     */
    public renderLayers() {
        if (!this.bitmapRenderer) {
            throw new Error(
                `Attempt to render Layers of TextureView that has no bitmapRenderer.`
            );
        }

        const width = this.size[0];
        const height = this.size[1];

        // A zero-area view (a pane that is not currently visible, or a
        // canvas that has not yet been laid out/measured) has nothing
        // meaningful to render — a crop of zero width or height cannot
        // be reduced to a smaller-but-valid crop, there is no image to
        // produce. `createImageBitmap` below rejects a zero width or
        // height outright (RangeError: "The crop rect width/height is
        // 0"), and since that call is fire-and-forget (no `.catch()`),
        // the rejection would otherwise surface as an unhandled promise
        // rejection rather than a handled no-op. Mirrors the same
        // zero-size guard in RoiPreviewRenderer.render.
        if (width === 0 || height === 0) {
            return;
        }

        const offscreenCanvas = gl.canvas as OffscreenCanvas;

        if (
            offscreenCanvas.width !== width ||
            offscreenCanvas.height !== height
        ) {
            offscreenCanvas.width = width;
            offscreenCanvas.height = height;
        }

        gl.viewport(0, 0, width, height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        compositeLayers(this.layers);
        for (const customObject of this._customObjects) {
            customObject.render(this);
        }
        for (const customObject of this.environment.customObjects) {
            customObject.render(this);
        }
        if (this.environment.operationPreviewStructure) {
            this.environment.operationPreviewStructure.render(this);
        }
        drawCrossSliceDiff(this, this.environment);
        gl.disable(gl.BLEND);

        // Snapshot exactly what was just composited, so it can be committed
        // atomically with the pixels below rather than re-read from a
        // possibly-newer environment when the async transfer resolves. Only
        // the images this view draws count: a view drawing one side of a
        // fused pair holds none of the other side's pixels.
        const drawnSlots = resolveFusedImageSlots(
            this.fusedImageDisplay,
            this.environment
        );
        const frameSources = [
            drawnSlots.primaryImage?.parent,
            drawnSlots.secondaryImage?.parent,
        ].flatMap((image) => (image ? [image] : []));

        createImageBitmap(offscreenCanvas, 0, 0, this.width, this.height).then(
            (bitmap) => {
                this.bitmapRenderer?.transferFromImageBitmap(bitmap);
                this._canvasFrameSources = frameSources;
                this._canvasFrameId++;
            }
        );
    }

    public updateView(
        layers: LayerBitMask = LayerBit.ALL_LAYERS
    ): boolean | undefined {
        if (!this.prepareContentUpdate()) {
            return undefined;
        }
        const contentUpdated = this.updateViewContent(layers);
        this.finishContentUpdate(contentUpdated);
        return contentUpdated;
    }

    public updateViewContent(layers: LayerBitMask): boolean {
        for (const layer of this) {
            if ((layer.layerBit & layers) == layer.layerBit) {
                layer.update(this.environment);
            }
        }
        return true;
    }

    /**
     * Method to update the {@link Layer.texture contents} of the {@link layers Layers} held within the {@link TextureView}.
     * @param env The {@link Environment} from which the {@link Layer Layers} read their targets.
     */
    protected updateLayers(env = this.env): boolean {
        if (!this._frameBufferObject) {
            return false;
        }
        this._frameBufferObject.bind();
        gl.viewport(
            0,
            0,
            this._frameBufferObject.width,
            this._frameBufferObject.height
        );
        let updated = false;
        for (const layer of this) {
            if (layer.update(env)) {
                updated = true;
            }
        }
        this._frameBufferObject.unbind();
        if (updated) {
            this.renderLayers();
        }
        return updated;
    }

    /**
     * Method to prepare this {@link TextureView} for rendering.\
     * This will bind the {@link _frameBufferObject FrameBufferObject} and set the {@link gl.viewport viewport}.
     * @returns True to indicate that the {@link _frameBufferObject FrameBufferObject} was successfully bound and viewport set.
     */
    public prepareContentUpdate(): boolean {
        if (!this._frameBufferObject) {
            throw new Error(
                `Attempt to update TextureView that does not have FrameBufferObject`
            );
        }
        this._frameBufferObject.bind();
        gl.viewport(0, 0, this.width, this.height);
        return true;
    }

    /**
     * Method to inform this {@link TextureView} about content update's finish.\
     * This will unbind the {@link _frameBufferObject FrameBufferObject} and\
     * if contentUpdated is true, render the layers on the {@link canvas}.
     * @param contentUpdated Boolean flag indicating that the contents were changed and {@link TextureView}'s {@link canvas} should be updated.
     */
    public finishContentUpdate(contentUpdated: boolean): void {
        if (!this._frameBufferObject) {
            throw new Error(
                `Attempt to finishContentUpdate of TextureView that does not have FrameBufferObject`
            );
        }
        this._frameBufferObject.unbind();
        if (contentUpdated) {
            this.renderLayers();
        }
    }

    public get width(): number {
        return this.size[0];
    }

    public get height(): number {
        return this.size[1];
    }

    /**
     * Method to delete the contents of this view.\
     * {@link ViewManager} will call this method when the view is to be deleted.
     */
    public delete(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this._frameBufferObject) {
            this._frameBufferObject.delete();
        }
        for (const layer of this) {
            layer.delete();
        }
    }

    /**
     * Views may define a default implementation for {@link EventHandler.onMousePress onMousePress}-event.\
     * MousePress event that was caught by the canvas and propagated by {@link ToolManager} to this view.
     * @param _button The button that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _canvasCoordinates The mouse position in canvas coordinates (0-1) at the time of the event
     * @param _view The view on which the event occured. Reference to this.
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this view reacted to that event and how to proceed.
     */
    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        _canvasCoordinates: vec2,
        _view: TextureView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Views may define a default implementation for {@link EventHandler.onMouseRelease onMouseRelease}-event.\
     * MouseRelease event that was caught by the canvas and propagated by {@link ToolManager} to this view.
     * @param _button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _canvasCoordinates The mouse position in canvas coordinates (0-1) at the time of the event
     * @param _view The view on which the event occured. Reference to this.
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this view reacted to that event and how to proceed.
     */
    public onMouseRelease(
        _button: number,
        _canvasCoordinates: vec2,
        _view?: TextureView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Views may define a default implementation for {@link EventHandler.onMouseMove onMouseMove}-event.\
     * MouseMove event that was caught by the canvas and propagated by {@link ToolManager} to this view.
     * @param _canvasCoordinates The mouse position in canvas coordinates (0-1) at the time of the event
     * @param _view The view on which the event occured. Reference to this.
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this view reacted to that event and how to proceed.
     */
    public onMouseMove(
        _canvasCoordinates: vec2,
        _buttons: number,
        _view: TextureView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Views may define a default implementation for {@link EventHandler.onMouseWheel onMouseWheel}-event.\
     * MouseWheel event that was caught by the canvas and propagated by {@link ToolManager} to this view.
     * @param _delta The direction of the MouseWheel event.
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _canvasCoordinates The mouse position in canvas coordinates (0-1) at the time of the event
     * @param _view The view on which the event occured. Reference to this.
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this view reacted to that event and how to proceed.
     */
    public onMouseWheel(
        _delta: -1 | 1,
        _modifierKeys: ModifierKeys,
        _canvasCoordinates: vec2,
        _view: TextureView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Views may define a default implementation for {@link EventHandler.onKeyPress onKeyPress}-event.\
     * KeyPress event that was caught by the canvas and propagated by {@link ToolManager} to this view.
     * @param _key The key that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _view The view on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this view reacted to that event and how to proceed.
     */
    public onKeyPress(
        _key: string,
        _modifierKeys: ModifierKeys,
        _view?: TextureView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    [Symbol.iterator](): Iterator<Layer<unknown>, Layer<unknown>, undefined> {
        // The `??` is defence in depth despite the non-optional type. The
        // constructor observes the canvas only after this field is assigned, so
        // no resize can be delivered while it is unset; the fallback costs
        // nothing and keeps a construction step added above the observer from
        // becoming a crash inside an iterator.
        return (this.layers ?? [])[Symbol.iterator]() as Iterator<
            Layer<unknown>,
            Layer<unknown>,
            undefined
        >;
    }
}
